// @owners { team: patients-team }
import { type FertilityCycleMedicationType } from '@alto/scriptdash/alto/fertility_cycles/types/v1/fertility_cycle_medication_type';
import { createSelector } from 'reselect';
import { type MedTypeByPrescriptionIDs, getMedTypeByPrescriptionID } from './getPrescriptionIDsByMedType';
import getProgynyWizardMedicationManagementData, {
  ADJUSTMENT_ERROR,
  ADJUSTMENT_REASON,
  type MedicationManagementData,
  type MedicationManagementDataByPrescriptionID,
} from './getProgynyWizardMedicationManagementData';
import { type ReduxStateShared } from '~shared/types';

export const QUANTITY_REVIEW_STATUS = {
  blocker_exceeded_allowed_days_supply: 'blocker_exceeded_allowed_days_supply',
  blocker_trigger_date: 'blocker_trigger_date',
  info_weekend_quantity_increase_trigger_date: 'info_weekend_quantity_increase_trigger_date',
  info_weekend_quantity_increase: 'info_weekend_quantity_increase',
};

export const QUANTITY_REVIEW_BLOCKERS = [
  QUANTITY_REVIEW_STATUS.blocker_exceeded_allowed_days_supply,
  QUANTITY_REVIEW_STATUS.blocker_trigger_date,
];

const QUANTITY_REVIEW_STATUS_PRIORITY = {
  [QUANTITY_REVIEW_STATUS.blocker_exceeded_allowed_days_supply]: 4,
  [QUANTITY_REVIEW_STATUS.blocker_trigger_date]: 3,
  [QUANTITY_REVIEW_STATUS.info_weekend_quantity_increase_trigger_date]: 2,
  [QUANTITY_REVIEW_STATUS.info_weekend_quantity_increase]: 1,
};

export type QuantityReviewStatus = keyof typeof QUANTITY_REVIEW_STATUS;

function quantityReviewStatus(
  medicationManagementData: MedicationManagementData | null | undefined,
): QuantityReviewStatus | null | undefined {
  const { adjustment_error: error, adjustment_reasons: reasons = [] } = medicationManagementData || {};

  if (error === ADJUSTMENT_ERROR.exceeded_allowed_days_supply) {
    // @ts-expect-error TS(2322): Type 'string' is not assignable to type '"blocker_exceeded_allowed_days_supply" | "blocker_trigger_d... (Delete me to see the full error)
    return QUANTITY_REVIEW_STATUS.blocker_exceeded_allowed_days_supply;
  }

  // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to parameter of type '"max_allowed_days_supply" | "stand... (Delete me to see the full error)
  if (error === ADJUSTMENT_ERROR.invalid_result && reasons.includes(ADJUSTMENT_REASON.trigger_date)) {
    // @ts-expect-error TS(2322): Type 'string' is not assignable to type '"blocker_exceeded_allowed_days_supply" | "blocker_trigger_d... (Delete me to see the full error)
    return QUANTITY_REVIEW_STATUS.blocker_trigger_date;
  }

  // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to parameter of type '"max_allowed_days_supply" | "stand... (Delete me to see the full error)
  if (reasons.includes(ADJUSTMENT_REASON.weekend_mail_order) && reasons.includes(ADJUSTMENT_REASON.trigger_date)) {
    // @ts-expect-error TS(2322): Type 'string' is not assignable to type '"blocker_exceeded_allowed_days_supply" | "blocker_trigger_d... (Delete me to see the full error)
    return QUANTITY_REVIEW_STATUS.info_weekend_quantity_increase_trigger_date;
  }

  // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to parameter of type '"max_allowed_days_supply" | "stand... (Delete me to see the full error)
  if (reasons.includes(ADJUSTMENT_REASON.weekend_mail_order)) {
    // @ts-expect-error TS(2322): Type 'string' is not assignable to type '"blocker_exceeded_allowed_days_supply" | "blocker_trigger_d... (Delete me to see the full error)
    return QUANTITY_REVIEW_STATUS.info_weekend_quantity_increase;
  }

  return null;
}

// powers the edge cases that show up on the quantity review page based on autobill results
// we may block the patient from scheduling or inform them about why their quantities were changed
// @ts-expect-error TS(2322): Type 'OutputSelector<State, string, (res: MedicationManagementDataByPrescriptionID) => string>' is n... (Delete me to see the full error)
export const getQuantityReviewStatus: (arg0: ReduxStateShared) => QuantityReviewStatus | null | undefined =
  createSelector(
    [getProgynyWizardMedicationManagementData],
    (medicationManagementData: MedicationManagementDataByPrescriptionID) => {
      // @ts-expect-error TS(2769): No overload matches this call.
      return Object.keys(medicationManagementData).reduce((status1, prescriptionID) => {
        const priority1 = (status1 && QUANTITY_REVIEW_STATUS_PRIORITY[status1]) || 0;
        const status2 = prescriptionID ? quantityReviewStatus(medicationManagementData[prescriptionID]) : null;
        const priority2 = (status2 && QUANTITY_REVIEW_STATUS_PRIORITY[status2]) || 0;
        return priority1 > priority2 ? status1 : status2;
      }, null);
    },
  );

export type MedicationBlockers = Record<string, FertilityCycleMedicationType>;

// powers the edge cases that show up on the quantity review page based on autobill results
// for certain blockers, we will explicitly show which of the medications are causing the blocker
export const getExceededDaysSupplyBlockerMeds: (arg0: ReduxStateShared) => MedicationBlockers = createSelector(
  [getProgynyWizardMedicationManagementData, getMedTypeByPrescriptionID],
  (
    medicationManagementData: MedicationManagementDataByPrescriptionID,
    medTypeByPrescriptionID: MedTypeByPrescriptionIDs,
  ) => {
    const blockers = {};
    Object.keys(medicationManagementData).forEach((prescriptionID) => {
      const status = quantityReviewStatus(medicationManagementData[prescriptionID]);

      if (
        status === QUANTITY_REVIEW_STATUS.blocker_exceeded_allowed_days_supply &&
        medTypeByPrescriptionID[prescriptionID]
      ) {
        // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expression of type 'string' can't be used to index type... (Delete me to see the full error)
        blockers[prescriptionID] = medTypeByPrescriptionID[prescriptionID];
      }
    });
    return blockers;
  },
);
